<template>
<div class="xy-chart-wrapper" :style="{
  '--xy-chart-height': chartHeight,
  '--xy-chart-min-height': chartMinHeight
}">
  <div class="xy-chart-header">
    <chart-ctrl-btn-group-v2
      :get-chart="showSelectedCtrlBtn ? getChart : null"
      :get-download-wrapper="showExportBtn ? getDownloadWrapper : null"
    >
      <template #toolbar-before>
        <slot name="toolbar-before" />
      </template>

      <template #toolbar>
        <slot name="toolbar" />
      </template>

      <template #toolbar-after>
        <slot name="toolbar-after" />
      </template>
    </chart-ctrl-btn-group-v2>
  </div>

  <div ref="download-wrapper" class="xy-chart-body">
    <div class="xy-chart-title">{{dataConfig.name}}</div>

    <div v-if="showLegend" class="xy-chart-legend-wrapper">
      <ul class="xy-chart-legends">
        <li
          v-for="(dimension, index) in dataConfig.dimensions"
          :key="dimension.id"
          :class="{
            active: selected[dimension.name]
          }"
          @click="onLegendClick(dimension)"
        >
        <span
          class="chart-legend-section"
          :chart-type="dimension.seriesParam.type"
          :class="{
            'custom-symbol': dimension.seriesParam.symbol ? true : false
          }"
          :style="{
            '--chart-legend-section-bg': dimension.seriesParam.color || COLORS[index % COLORS.length],
            '--chart-legend-custom-symbol': dimension.seriesParam.symbol ? `url(${dimension.seriesParam.symbol.replace(/^image:\/\//, '')})` : ''
          }"
        ><i></i></span>
          <span class="chart-legend-name">{{dimension.name}}</span>
        </li>
      </ul>
    </div>

    <div class="xy-chart">
      <e-chart ref="chart" @initialized="onChartInitialized" @destroy="onChartDestroy" />
    </div>
  </div>
</div>
</template>

<script>
import ChartCtrlBtnGroupV2 from './ChartCtrlBtnGroupV2'
import EChart from '../Charts/ECharts'
import ChartStyle from './ChartStyle'

const getUnit = (isPercentage, dimension, unitProp) => isPercentage && dimension.seriesParam.stack ? '%' : dimension[unitProp]

export default {
  name: 'XYChart',
  components: {ChartCtrlBtnGroupV2, EChart},
  props: {
    index: {
      type: Number
    },
    isShowLine: {
      default: false,
      type: Boolean
    },
    dataConfig: {
      required: true,
      type: Object
    },
    unitProp: {
      required: true,
      type: String
    },
    dataTimeFormat: {
      type: String,
      // default: 'YY年MM月DD日HH时'
      default: 'YY/MM/DD HH:mm'
    },
    chartType: {
      required: true,
      type: String
    },
    isShowBar: {
      type: Boolean
    },
    stackType: {
      type: Number,
      default: 1
    },
    chartMinHeight: {
      type: String,
      default: '200px'
    },
    chartHeight: {
      type: String,
      default: '300px'
    },
    downloadName: {
      required: true,
      type: String
    },
    selectedChangeWithDataChanged: {
      type: Boolean
    },
    showSelectedCtrlBtn: {
      type: Boolean,
      default: true
    },
    showExportBtn: {
      type: Boolean,
      default: true
    },
    needResize: {
      type: Boolean
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    clickHandler: {
      type: Function
    },
    yAxisScale: {
      type: Boolean,
      default: true
    },
    minMax: {
      type: Object,
      default: () => ({})
    },
    maxLegendNum: {
      type: Number
    }
  },
  data () {
    return {
      selected: this.buildSelected()
    }
  },
  computed: {
    COLORS: () => ChartStyle.COLORS,
    chartDownloadName () {
      let {dataConfig, downloadName} = this
      let {name, dataTimes} = dataConfig
      return `${name}${downloadName ? `_${downloadName}` : ''}_${new Date(dataTimes[0][0]).format('YYYY年MM月DD日HH时mm分')}-${new Date(dataTimes[dataTimes.length - 1][0]).format('YYYY年MM月DD日HH时mm分')}`
    },
    dimensionMap () {
      return this.dataConfig.dimensions.reduce((map, dimension) => {
        map[dimension.id] = dimension
        return map
      }, {})
    },
    isPercentage () {
      return this.stackType === 2
    },
    units () {
      let {unitProp} = this
      let {dimensions} = this.dataConfig
      let units = [...new Set(dimensions.map(dimension => dimension[unitProp]))]

      if (units.indexOf('%') === -1) {
        units.push('%')
      }

      return units
    }
  },
  methods: {
    buildSelected () {
      let {selectedNames, dimensions} = this.dataConfig
      return selectedNames ? {
        ...selectedNames
      } : dimensions.reduce((map, dimension, index) => {
        if (this.maxLegendNum && this.maxLegendNum > 0) {
          if (index < this.maxLegendNum) {
            map[dimension.name] = true
          } else {
            map[dimension.name] = false
          }
        } else {
          map[dimension.name] = true
        }
        return map
      }, {})
    },
    render (isReal) {
      let chartParam = this.buildChartParam(isReal)
      let toolbox
      if (this.isShowLine) {
        toolbox = {
          feature: {
            magicType: { show: true, type: ['line', 'bar'] }
          },
          itemSize: 20,
          right: 23
        }
      }
      let chartOpt = {
        index: this.index,
        toolbox: toolbox,
        tooltip: {
          trigger: 'axis',
          appendToBody: true,
          formatter: params => {
            const E_CHART_MAX_ROWS = process.env.E_CHART_MAX_ROWS
            let columns = Math.ceil(params.length / E_CHART_MAX_ROWS)
            let {dimensionMap, unitProp, isPercentage} = this
            let add = 1
            return `<div class="e-chart-tooltip" style="--e-chart-tooltip-columns:${columns}"><div class="e-chart-tooltip-name">${params[0].name}</div><div class="e-chart-tooltip-content">${params.map(({marker, seriesName, value, seriesId, data, name}, index) => {
              let dimension = dimensionMap[seriesId]
              let {getTooltip} = dimension

              if (typeof getTooltip === 'function') {
                // ${data.isAuditCompleted ? '(已审核)' : '(未审核)'}
                let tooltipData = getTooltip(dimension, marker, seriesName, value, seriesId, data, unitProp, columns, add, name)

                return tooltipData.map((tooltipData, subIndex) => {
                  if (subIndex > 0) {
                    add++
                  }

                  // if (tooltipData.isHaveIntegral) {
                  //   return `${tooltipData.marker}<span class="e-chart-tooltip-series-name">${tooltipData.seriesName}:</span><span class="e-chart-tooltip-value ${((index + add) % columns) === 0 ? '' : 'e-chart-tooltip-value-space'}">${Number.isFinite(tooltipData.value) ? `${tooltipData.value}${tooltipData.unit || ''}${!tooltipData.isHaveIntegral && tooltipData.dFlag ? `(${tooltipData.dFlag.split('|').map(flag => DAS_FLAG_MAP[flag] || flag)})` : ''}` : (tooltipData.noDataReason ? tooltipData.noDataReason : (data && data.noDataReason ? data.noDataReason : `--${tooltipData.unit || ''}`))}${tooltipData.isHaveIntegral ? '(重积分)' : ''}</span>`
                  // }

                  // return `${tooltipData.marker}<span class="e-chart-tooltip-series-name">${tooltipData.seriesName}:</span><span class="e-chart-tooltip-value ${((index + add) % columns) === 0 ? '' : 'e-chart-tooltip-value-space'}">${Number.isFinite(parseFloat(tooltipData.value)) ? `${tooltipData.value}${tooltipData.unit || ''}${!tooltipData.isHaveIntegral && tooltipData.dFlag ? `(${tooltipData.dFlag.split('|').map(flag => DAS_FLAG_MAP[flag] || flag)})` : ''}` : (tooltipData.noDataReason ? tooltipData.noDataReason : (data && data.noDataReason ? data.noDataReason : `--${tooltipData.unit || ''}`))}</span>`
                  const {displayFlag, value} = tooltipData
                  return `${tooltipData.marker}<span class="e-chart-tooltip-series-name">${tooltipData.seriesName}:</span><span class="e-chart-tooltip-value ${((index + add) % columns) === 0 ? '' : 'e-chart-tooltip-value-space'}">${Number.isFinite(parseFloat(value)) || (typeof value === 'string' && value.length > 0) ? `${value}${tooltipData.unit || ''}${displayFlag ? `(${displayFlag})` : ''}` : (displayFlag || '--')}</span>`
                }).join('')
              } else {
                data = data || {}
                const displayFlag = data[3]
                // return `${marker}<span class="e-chart-tooltip-series-name">${seriesName}:</span><span class="e-chart-tooltip-value ${((index + add) % columns) === 0 ? '' : 'e-chart-tooltip-value-space'}">${Number.isFinite(value) ? Number(value.toFixed(3)) : (data && data.displayFlag ? data.displayFlag : '--')}${dimension.tooltipShowUnit === false ? '' : getUnit(isPercentage, dimensionMap[seriesId], unitProp)}${(data && data.isHaveIntegral) ? '(重积分)' : ''}${((!data || !data.isHaveIntegral) && dFlag) ? `(${dFlag.split('|').map(flag => DAS_FLAG_MAP[flag] || flag)})` : ''}</span>`
                return `${marker}<span class="e-chart-tooltip-series-name">${seriesName}:</span><span class="e-chart-tooltip-value ${((index + add) % columns) === 0 ? '' : 'e-chart-tooltip-value-space'}">${Number.isFinite(value[1]) ? `${Number(value[1].toFixed(3))}${dimension.tooltipShowUnit === false ? '' : getUnit(isPercentage, dimensionMap[seriesId], unitProp)}${displayFlag ? `(${displayFlag})` : ''}` : (displayFlag || '--')}</span>`
              }
            }).join('')}</div></div>`
          }
        },
        ...chartParam
      }

      let {chart} = this.$refs

      setTimeout(() => {
        if (this.needResize) {
          chart.resize()
        }

        chart.setOpt(chartOpt, isReal)
      }, 0)
    },
    buildChartParam (isReal) {
      let {yAxisScale, minMax} = this
      let yAxisMap = this.units.reduce((map, unit, index) => {
        map[unit] = {
          index,
          show: false,
          type: 'value',
          name: unit,
          axisLine: {
            show: true
          },
          splitLine: {
            show: false
          },
          axisTick: {
            show: true
          },
          axisLabel: {
            fontFamily: ChartStyle.NUMBER_FONT_FAMILY
          },
          scale: yAxisScale,
          ...(minMax[unit] || {})
        }

        return map
      }, {})

      let {dataConfig, COLORS, isPercentage, unitProp} = this
      let {dimensions, dataTimes, seriesMap} = dataConfig
      let COLORS_LEN = COLORS.length
      let {selected} = this
      // let dimensionsNameMap = dimensions.reduce((map, el) => {
      //   map[el.name] = el
      //   return map
      // }, {})

      // let showSeriesDataTime = {}
      // for (let key in selected) {
      //   if (selected[key]) {
      //     let id = dimensionsNameMap[key].id
      //     showSeriesDataTime[id] = Object.keys(seriesMap[id])
      //   }
      // }

      // let newDataTimes = []
      // for (let key in showSeriesDataTime) {
      //   if (showSeriesDataTime[key].length >= newDataTimes.length) {
      //     newDataTimes = showSeriesDataTime[key]
      //   }
      // }

      let series = dimensions.map((dimension, index) => {
        let {id} = dimension
        let dataMap = seriesMap[id]

        if (dimension.id === '1012') {
          return {
            id,
            name: dimension.name,
            yAxisIndex: yAxisMap[getUnit(isPercentage, dimension, unitProp)].index,
            color: COLORS[index % COLORS_LEN],
            data: dataTimes.map(dataTime => dataMap[dataTime[1]]),
            ...dimension.seriesParam
          }
        } else {
          return {
            id,
            name: dimension.name,
            yAxisIndex: yAxisMap[getUnit(isPercentage, dimension, unitProp)].index,
            color: COLORS[index % COLORS_LEN],
            data: Object.values(dataMap).map(el => {
              return [el.dataTime, el.value, el.factorId, el.displayFlag]
            }),
            ...dimension.seriesParam,
            smooth: true,
            symbolSize: 16
          }
        }
      })

      if (this.isPercentage) {
        this.setPercentageData(dataTimes, series, selected)
      }

      let legend = {
        show: false,
        selected: {
          ...selected
        }
      }

      let gridBottom = 20
      let grid = {
        top: 30,
        bottom: gridBottom + 58,
        left: ChartStyle.Y_AXIS_GROUP_INTERVAL,
        containLabel: false
      }

      let dataTimesLen = dataTimes.length
      let dataZoomStart = dataTimesLen <= 72 ? 0 : ((dataTimesLen - 72) / dataTimesLen * 100).toFixed(0)
      let dataZoomEnd = 100

      if (isReal) {
        let chartOpt = this.$refs.chart.getOpt()
        let {dataZoom: [dataZoom]} = chartOpt
        dataZoomStart = dataZoom.start
        dataZoomEnd = dataZoom.end
      }

      let dataZoom = [{
        type: 'inside',
        xAxisIndex: 0,
        minSpan: 5,
        start: dataZoomStart,
        end: dataZoomEnd
      }, {
        type: 'slider',
        realtime: true,
        start: dataZoomStart,
        end: dataZoomEnd,
        xAxisIndex: 0,
        minSpan: 5,
        bottom: gridBottom,
        left: ChartStyle.Y_AXIS_GROUP_INTERVAL,
        right: ChartStyle.GRID_DEFAULT_RIGHT,
        // handleIcon: 'M10.7,11.9H9.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4h1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
        handleSize: '70%',
        handleStyle: {
          color: '#fff',
          shadowBlur: 4,
          shadowColor: 'rgba(0, 0, 0, 0.15)',
          shadowOffsetX: 2,
          shadowOffsetY: 2
        },
        // dataBackground: {
        //   areaStyle: '#E4F0FF'
        // }
        fillerColor: 'rgba(228, 240, 255, .6)',
        borderColor: '#E4F0FF'
      }]

      let yAxis = Object.values(yAxisMap)
      this.setYAxisDisplay(selected, grid, yAxis, dataZoom)

      let {dataTimeFormat} = this

      // newDataTimes
      return {
        legend,
        grid,
        xAxis: {
          type: 'category',
          data: dataTimes.map(dataTime => dataTime[1]),
          axisTick: {
            alignWithLabel: true
          },
          axisLabel: {
            fontFamily: ChartStyle.TEXT_FONT_FAMILY,
            formatter: dataTime => dataTimeFormat ? new Date(dataTime).format(dataTimeFormat) : dataTime
          }
        },
        yAxis,
        series,
        dataZoom
      }
    },
    setYAxisDisplay (selected, grid, yAxis, dataZoom) {
      let {dataConfig: {dimensions}, unitProp, isPercentage} = this
      let displayDimensions = dimensions.filter(dimension => selected[dimension.name])
      let unitMap = displayDimensions.reduce((map, dimension) => {
        map[getUnit(isPercentage, dimension, unitProp)] = true
        return map
      }, {})

      yAxis.forEach(yAxisItem => {
        yAxisItem.show = unitMap[yAxisItem.name] === true
      })

      let filterYAxis = yAxis.filter(yAxisItem => yAxisItem.show)
      filterYAxis.forEach((yAxisItem, index) => {
        yAxisItem.splitLine.show = index === 0
        yAxisItem.position = index === 0 ? 'left' : 'right'
        yAxisItem.offset = index < 2 ? 0 : ((index - 1) * 65)
        yAxisItem.max = yAxisItem.name === '%' ? 100 : yAxisItem.max
      })

      let filterYAxisLen = filterYAxis.length
      grid.right = dataZoom[dataZoom.length - 1].right = filterYAxisLen <= 1 ? ChartStyle.GRID_DEFAULT_RIGHT : (filterYAxis.length - 1) * ChartStyle.Y_AXIS_GROUP_INTERVAL
    },
    setPercentageData (dataTimes, series, selected) {
      let dataTimesLen = dataTimes.length
      let stackGroups = Object.values(series.reduce((map, seriesItem) => {
        let {stack} = seriesItem

        if (stack && selected[seriesItem.name]) {
          let series = map[stack]

          if (!series) {
            series = map[stack] = []
          }

          series.push(seriesItem)
        }

        return map
      }, {}))

      stackGroups.forEach(series => {
        let seriesLen = series.length

        for (let i = 0; i < dataTimesLen; i++) {
          let values = []

          for (let j = 0; j < seriesLen; j++) {
            let data = series[j].data[i]

            if (data) {
              values.push([j, data.value, data.isHaveIntegral, data.noDataReason, data.displayFlag])
            }
          }

          if (values.length > 0) {
            let total = values.reduce((total, value) => Number.isFinite(value[1]) ? (total || 0) + value[1] : total, null)

            values.forEach(([index, value, isHaveIntegral, noDataReason, displayFlag]) => {
              series[index].data[i] = {
                value: Number.isFinite(value) ? Number((value / total * 100).toFixed(2)) : null,
                isHaveIntegral: isHaveIntegral,
                noDataReason,
                displayFlag
              }
            })
          }
        }
      })
    },
    onLegendClick (dimension) {
      let {selected} = this
      let {name} = dimension
      if (this.maxLegendNum && this.maxLegendNum > 0) {
        if (Object.values(selected).filter(el => el).length < this.maxLegendNum || selected[name]) {
          selected[name] = !selected[name]
          this.render(true)
        }
      } else {
        selected[name] = !selected[name]
        this.render(true)
      }
      // let chart = this.$refs.chart
      // let chartOpt = chart.getOpt()
      // let {legend: [legend], grid: [grid], yAxis, dataZoom} = chartOpt
      // legend.selected = {...selected}
      // this.setYAxisDisplay(selected, grid, yAxis, dataZoom)
      // chart.refreshOpt({
      //   legend,
      //   yAxis,
      //   grid,
      //   dataZoom
      // })
    },
    onChartClick (param) {
      let {clickHandler} = this
      let opt = this.$refs.chart.getOpt()

      if (clickHandler) {
        let {chart} = this.$refs

        setTimeout(() => {
          chart.resize()
        }, clickHandler(param, opt))
      }
    },
    onChartInitialized (chartObj) {
      chartObj.on('click', this.onChartClick)
      this.initialized = true
      this.render()
    },
    onChartDestroy (chartObj) {
      chartObj.off('click', this.onChartClick)
      this.initialized = false
    },
    renderChart () {
      this.render(true)
    },
    getChart () {
      return {
        chart: this.$refs.chart,
        selected: this.selected,
        render: this.renderChart
      }
    },
    getDownloadWrapper () {
      return {
        downloadWrapper: this.$refs['download-wrapper'],
        downloadName: this.chartDownloadName
      }
    },
    getChartComponent () {
      return this.$refs.chart
    }
  },
  watch: {
    dataConfig () {
      if (this.selectedChangeWithDataChanged) {
        this.selected = this.buildSelected()
      }

      if (this.initialized) {
        this.render()
      }
    },
    chartType () {
      if (!this.initialized) {
        return
      }

      let chart = this.$refs.chart
      let chartOpt = chart.getOpt()
      let {series} = chartOpt
      let map = series.reduce((map, seriesItem) => {
        map[seriesItem.id] = seriesItem
        return map
      }, {})

      chart.refreshOpt({
        series: this.dataConfig.dimensions.map(dimension => Object.assign(map[dimension.id], dimension.seriesParam))
      })
    },
    stackType () {
      if (this.initialized) {
        this.render(true)
      }
    },
    isShowBar () {
      this.render(true)
    }
  }
}
</script>

<style lang="scss">
.xy-chart-wrapper {
  display: grid;
  grid-template-rows: max-content auto;
  grid-row-gap: 10px;

  > * {
    min: {
      width: 0;
      height: 0;
    };
    width: 100%;
  }

  > .xy-chart-header {
    display: flex;
    justify-content: center;
    gap: 20px;
  }

  > .xy-chart-body {
    display: grid;
    grid-template-rows: max-content max-content var(--xy-chart-height);
    grid-row-gap: 8px;

    > * {
      min: {
        width: 0;
        height: 0;
      };
      width: 100%;
    }
  }

  .xy-chart-title {
    font: {
      size: 18px;
      weight: bolder;
    };
    text-align: center;
  }

  .xy-chart-legend-wrapper {
    display: flex;
    justify-content: center;
  }

  .xy-chart {
    grid-row: 3;
    min-height: var(--xy-chart-min-height);
  }

  .xy-chart-legends {
    font: {
      size: 14px;
      family: '宋体';
    };
    display: flex;
    flex-wrap: wrap;
    max-width: 85%;
    gap: 10px;
    margin: 0;
    padding: 0;
    color: #333333;
    user-select: none;

    > li {
      display: grid;
      grid-template-columns: 25px max-content;
      grid-column-gap: 5px;
      align-items: center;
      height: 1em;
      line-height: 1;
      cursor: pointer;

      &.active {
        > .chart-legend-section {
          &:before,
          &:after {
            background-color: currentColor !important
          }

          &:before,
          &:after,
          > i {
            border-color: currentColor !important;
          }

          &[chart-type="line"] {
            > i {
              opacity: 1 !important;
              filter: grayscale(0) !important;

              &:before {
                background-color: currentColor !important;
              }
            }
          }

          &[chart-type="bar"] {
            > i {
              background-color: currentColor !important;
            }
          }
        }

        > .chart-legend-name {
          color: inherit;
        }
      }

      > .chart-legend-section {
        height: 100%;
        color: var(--chart-legend-section-bg);

        &:before,
        &:after {
          background-color: #cccccc;
        }

        > i {
          height: 100%;
          border: 1px solid #cccccc;
        }

        &[chart-type="bar"] {
          display: flex;

          > i {
            flex: 1;
            border: {
              radius: 5px;
            };
            background-color: #cccccc;
          }
        }

        &[chart-type="line"] {
          display: grid;
          grid-template-columns: 1fr 1em 1fr;
          align-items: center;

          &:before,
          &:after {
            content: "";
            height: 2px;
          }

          > i {
            border: 2px solid #cccccc {
              radius: 50%;
            };
          }

          &.custom-symbol {
            > i {
              display: flex;
              justify-content: center;
              align-items: center;
              border: 0 {
                radius: 0;
              };
              background: {
                image: var(--chart-legend-custom-symbol);
                position: center;
                size: contain;
                repeat: no-repeat;
              };
              filter: grayscale(1);
              fill-opacity: 50%;
              opacity: .5;

              &:before {
                content: "";
                width: 100%;
                height: 2px;
                background-color: currentColor;
              }
            }
          }
        }
      }

      > .chart-legend-name {
        font-size: 12px;
        color: #ccc;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/DataChart/XYChartV2.vue